/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { useState } from 'react';
import { Button } from 'reactstrap';
import StandardsFrameworks from './StandardsFrameworks';
import { getStandardButtons } from './getStandardButtons';
import { getFrameworkButtons } from './getFrameworkButtons';
import { QUESTION } from '@constants/terminology';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { SurveyModelMinData } from '@g17eco/types/survey';
import UniversalTrackerClassInterface from '@models/UniversalTrackerClassInterface';

interface QuestionSubtitleProps {
  utr?: UniversalTrackerClassInterface;
  selectedStandard: string;
  handleStandardsClick: (code: string) => void;
  survey: Pick<SurveyModelMinData, 'scope'>;
}

interface GlobalQuestionSubtitleProps {
  displayStandard: string;
  utr?: UniversalTrackerClassInterface;
  selectedStandard: string;
  isOpen: boolean;
  handleOpen: (value: boolean) => void;
  handleStandardsClick: (code: string) => void;
  survey: Pick<SurveyModelMinData, 'scope'>;
}

const QuestionStandardSubtitle = (props: GlobalQuestionSubtitleProps): JSX.Element => {
  const { utr, selectedStandard, isOpen, handleOpen, handleStandardsClick, survey } = props;

  if (!utr) {
    return <></>;
  }

  const standardsFrameworksProps = {
    utr,
    standardShow: 4,
    selectedStandard,
    showSelectedStandard: Boolean(selectedStandard) && !isOpen,
    handleStandardsClick,
    survey
  };
  const numOfStandardsFrameworks =
    getStandardButtons(standardsFrameworksProps).length + getFrameworkButtons(standardsFrameworksProps).length;

  return (
    <div className='d-relative question-subtitle-container d-flex align-items-end mb-2'>
      <div className={`ctlight-standards-frameworks-container ${isOpen ? 'expand' : ''}`}>
        <StandardsFrameworks {...standardsFrameworksProps} />
      </div>
      {numOfStandardsFrameworks > 1 && (
        <SimpleTooltip text={`Switch this ${QUESTION.SINGULAR} between standards and view related frameworks`}>
          <Button
            color='link'
            onClick={() => handleOpen(!isOpen)}
            className={`ctlight-toggle-button ${isOpen ? 'active' : ''}`}
          >
            <i className={`fa-solid ${isOpen ? 'fa-caret-left' : 'fa-caret-right'}`}></i>
          </Button>
        </SimpleTooltip>
      )}
    </div>
  );
};

export default function QuestionSubtitle({ utr, selectedStandard, handleStandardsClick, survey }: QuestionSubtitleProps) {
  const [isOpen, setOpen] = useState(false);

  if (!utr) {
    return <></>;
  }

  const standardName = utr.getTypeName(selectedStandard);
  const standardCode = utr.getTypeCode(selectedStandard) || '';
  const displayStandard =
    standardName && standardCode ? `${standardName} - ${standardCode}` : standardName || standardCode;
  const subProps = {
    displayStandard,
    utr,
    selectedStandard,
    isOpen,
    handleOpen: (value: boolean) => setOpen(value),
    handleStandardsClick,
    survey
  };

  return <QuestionStandardSubtitle {...subProps} />;
}

